import React, { useState, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom"; // Add useLocation here
import { AnimatePresence } from "framer-motion";
import Homepage from "./Homepage";
import BlogPage from "./pages/blog/index";
import BlogPost from "./pages/blog/Post";
import Explain from "./pages/vysvetlujeme/Explain";
import { ReactLenis } from "@studio-freight/react-lenis";
import { SearchProvider } from "./Components/SearchContext";
import SearchResults from "./Components/SearchResults";
import ConfirmSubscription from "./Components/ConfirmSubscription";
import Tos from "./pages/Tos";
import Dakujeme from "./pages/Dakujeme";
import DakujemeAI from "./pages/DakujemeAI";
/* import ReactMediorJob from "./pages/job/react-medior-2025";
import AiConsultantJob from "./pages/job/ai-consultant-2025"; */
import { HelmetProvider } from "react-helmet-async";
import { initCookieConsent, updateCookieConsentLanguage } from "./cookieConsent";
import { initGoogleTagManager, trackPageView } from "./utils/analytics";
import { initPostHog, trackPostHogPageView } from "./utils/posthog";
import { ConversionProvider } from "./contexts/ConversionContext";
import MarketingPrePodlahy from "./pages/marketing-pre-podlahy";
import MarketingPreVelkoobchodneSpolocnosti from "./pages/marketing-pre-velkoobchodne-spolocnosti";
import MarketingPreOtcLiekyVolnopredajneDoplnky from "./pages/marketing-pre-otc-lieky-volnopredajne-doplnky";
import MarketingPreStavebnictvo from "./pages/marketing-pre-stavebnictvo";
import MarketEntryStrategySlovakiaCzechRepublic from "./pages/market-entry-strategy-slovakia-czechrepublic";
import BioLinksPage from "./pages/BioLinksPage";
import MarketingLanding from "./Components/Marketing/MarketingLanding";
import { sectorPodlahy } from "./Components/Marketing/data/podlahy";

const App = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const location = useLocation(); // Add this for route tracking

  useEffect(() => {
    // Only initialize if user has consented (if using cookie consent)
    const hasConsented = localStorage.getItem("analytics_consent") === "true";
    if (hasConsented) {
      initGoogleTagManager();
      initPostHog();
    }
  }, []);

  // For tracking page views
  useEffect(() => {
    trackPageView(location.pathname + location.search);
    trackPostHogPageView(location.pathname + location.search);
  }, [location]);

  // Initialize cookie consent when component mounts
  useEffect(() => {
    console.log("Setting up cookie consent");
    initCookieConsent()
      .then((success) => {
        if (success) {
          console.log("Cookie consent successfully initialized");
        } else {
          console.error("Failed to initialize cookie consent");
        }
      })
      .catch((error) => {
        console.error("Error during cookie consent initialization:", error);
      });
  }, []);

  // Update cookie consent language when location changes
  useEffect(() => {
    updateCookieConsentLanguage();
  }, [location.pathname]);

  return (
    <HelmetProvider>
      <ConversionProvider>
        <SearchProvider>
          <ReactLenis
            root
            options={{
              lerp: 0.1,
              duration: 1.2,
              smoothWheel: true,
              smoothTouch: false,
              touchMultiplier: 2,
            }}
          >
          <AnimatePresence mode='wait'>
            <Routes>
              {/* English routes (with /en prefix) - must come FIRST to avoid conflicts */}
              <Route path='/en' element={<Homepage />} />
              <Route path='/en/blog' element={<BlogPage />} />
              <Route path='/en/blog/:slug' element={<BlogPost />} />
              <Route path='/en/vysvetlujeme/:slug' element={<Explain />} />
              <Route path='/en/confirm' element={<ConfirmSubscription />} />
              <Route path='/en/dakujeme' element={<Dakujeme />} />
              <Route path='/en/tos' element={<Tos />} />
              <Route path='/en/marketing-pre-podlahy' element={<MarketingPrePodlahy />} />
              <Route path='/en/marketing-pre-velkoobchodne-spolocnosti' element={<MarketingPreVelkoobchodneSpolocnosti />} />
              <Route path='/en/marketing-pre-otc-lieky-volnopredajne-doplnky' element={<MarketingPreOtcLiekyVolnopredajneDoplnky />} />
              <Route path='/en/marketing-pre-stavebnictvo' element={<MarketingPreStavebnictvo />} />
              <Route path='/en/market-entry-strategy-slovakia-czechrepublic' element={<MarketEntryStrategySlovakiaCzechRepublic />} />
              <Route path='/en/bio' element={<BioLinksPage />} />

              {/* Polish routes (with /pl prefix) */}
              <Route path='/pl' element={<Homepage />} />
              <Route path='/pl/blog' element={<BlogPage />} />
              <Route path='/pl/blog/:slug' element={<BlogPost />} />
              <Route path='/pl/vysvetlujeme/:slug' element={<Explain />} />
              <Route path='/pl/confirm' element={<ConfirmSubscription />} />
              <Route path='/pl/dakujeme' element={<Dakujeme />} />
              <Route path='/pl/tos' element={<Tos />} />
              <Route path='/pl/marketing-pre-podlahy' element={<MarketingPrePodlahy />} />
              <Route path='/pl/marketing-pre-velkoobchodne-spolocnosti' element={<MarketingPreVelkoobchodneSpolocnosti />} />
              <Route path='/pl/marketing-pre-otc-lieky-volnopredajne-doplnky' element={<MarketingPreOtcLiekyVolnopredajneDoplnky />} />
              <Route path='/pl/marketing-pre-stavebnictvo' element={<MarketingPreStavebnictvo />} />
              <Route path='/pl/market-entry-strategy-slovakia-czechrepublic' element={<MarketEntryStrategySlovakiaCzechRepublic />} />
              <Route path='/pl/bio' element={<BioLinksPage />} />

              {/* Czech routes (with /cs prefix) */}
              <Route path='/cs' element={<Homepage />} />
              <Route path='/cs/blog' element={<BlogPage />} />
              <Route path='/cs/blog/:slug' element={<BlogPost />} />
              <Route path='/cs/vysvetlujeme/:slug' element={<Explain />} />
              <Route path='/cs/confirm' element={<ConfirmSubscription />} />
              <Route path='/cs/dakujeme' element={<Dakujeme />} />
              <Route path='/cs/tos' element={<Tos />} />
              <Route path='/cs/marketing-pre-podlahy' element={<MarketingPrePodlahy />} />
              <Route path='/cs/marketing-pre-velkoobchodne-spolocnosti' element={<MarketingPreVelkoobchodneSpolocnosti />} />
              <Route path='/cs/marketing-pre-otc-lieky-volnopredajne-doplnky' element={<MarketingPreOtcLiekyVolnopredajneDoplnky />} />
              <Route path='/cs/marketing-pre-stavebnictvo' element={<MarketingPreStavebnictvo />} />
              <Route path='/cs/market-entry-strategy-slovakia-czechrepublic' element={<MarketEntryStrategySlovakiaCzechRepublic />} />
              <Route path='/cs/bio' element={<BioLinksPage />} />

              {/* Slovak routes (default - no language prefix) */}
              <Route path='/' element={<Homepage />} />
              <Route path='/blog' element={<BlogPage />} />
              <Route path='/blog/:slug' element={<BlogPost />} />
              <Route path='/vysvetlujeme/:slug' element={<Explain />} />
              <Route path='/confirm' element={<ConfirmSubscription />} />
              <Route path='/dakujeme' element={<Dakujeme />} />
              <Route path='/dakujeme-ai' element={<DakujemeAI />} />
              <Route path='/tos' element={<Tos />} />
              <Route path='/marketing-pre-podlahy' element={<MarketingPrePodlahy />} />
              <Route path='/v2/marketing-pre-podlahy' element={<MarketingLanding sector={sectorPodlahy} />} />
              <Route path='/marketing-pre-velkoobchodne-spolocnosti' element={<MarketingPreVelkoobchodneSpolocnosti />} />
              <Route path='/marketing-pre-otc-lieky-volnopredajne-doplnky' element={<MarketingPreOtcLiekyVolnopredajneDoplnky />} />
              <Route path='/marketing-pre-stavebnictvo' element={<MarketingPreStavebnictvo />} />
              <Route path='/market-entry-strategy-slovakia-czechrepublic' element={<MarketEntryStrategySlovakiaCzechRepublic />} />
              <Route path='/bio' element={<BioLinksPage />} />

              {/*  <Route path='/job/react-medior-2025' element={<ReactMediorJob />} />
              <Route path='/job/ai-consultant-2025' element={<AiConsultantJob />} />
              <Route path='/en/job/react-medior-2025' element={<ReactMediorJob />} />
              <Route path='/en/job/ai-consultant-2025' element={<AiConsultantJob />} /> */}
            </Routes>
          </AnimatePresence>
          </ReactLenis>
          <SearchResults
            results={searchResults}
            isVisible={isSearchVisible}
            onClose={() => setIsSearchVisible(false)}
          />
        </SearchProvider>
      </ConversionProvider>
    </HelmetProvider>
  );
};

export default App;
